import React, { useCallback, useEffect, useState } from 'react';
import {
  DesignDocument,
  DesignRun,
  RealizationTarget,
  designRunsService,
} from '../../../services/designRunsService';
import { CourseWorkspace } from '../sections/CourseGeneration/CourseWorkspace';
import { PipelineCard } from '../shared/PipelineCard';
import { Chip } from './editors';
import {
  AssetsDetail,
  DetailPanel,
  DraftingDetail,
  LevelCheckDetail,
  OutlineDetail,
  PackageDetail,
  ValidateDetail,
  VideosDetail,
} from './RenderDetails';
import { SIMON_STEPS, simonStageStates } from './simonPipeline';

const NAVY = '#1a2332';

function RenderButton({ label, disabled, onClick }: {
  label: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button onClick={onClick} disabled={disabled}
      className="px-3 py-1.5 rounded-md text-sm text-white hover:opacity-90 disabled:opacity-50"
      style={{ backgroundColor: NAVY }}>
      {label}
    </button>
  );
}

/** Render status for one course. One tab per target; each tab shows that
    target's pipeline in the classic Course Generation layout. The Talaera tab
    IS the classic course workspace (same components), pinned to the realized
    course run. Only reachable once the design is approved. */
export function RenderStatusPage({ runId, courseTitle, onBack }: {
  runId: string;
  courseTitle: string;
  onBack: () => void;
}) {
  const [run, setRun] = useState<DesignRun | null>(null);
  const [doc, setDoc] = useState<DesignDocument | null>(null);
  const [activeTarget, setActiveTarget] = useState<RealizationTarget | null>(null);
  const [simonStageId, setSimonStageId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setRun(await designRunsService.get(runId));
      setDoc(await designRunsService.getDocument(runId));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [runId]);

  // Always poll while this page is open: a just-clicked render has no
  // realization entry yet, so status-conditional polling would never arm.
  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, 5000);
    return () => clearInterval(timer);
  }, [refresh]);

  // Defaults once the run arrives: open the target that has work, and land
  // Simon on the Build Package when it's ready.
  useEffect(() => {
    if (!run) return;
    if (!activeTarget) {
      setActiveTarget(run.realizations?.simon?.status || !run.realizations?.talaera?.status
        ? 'simon' : 'talaera');
    }
    if (!simonStageId && run.realizations?.simon?.status) {
      const failedValidation = run.realizations.simon.status === 'failed'
        && doc?.realizations?.simon?.validation?.validated === false;
      setSimonStageId(failedValidation ? 'simon_validate'
        : run.realizations.simon.status === 'package_ready' ? 'simon_compile'
        : 'simon_outline');
    }
  }, [run, doc, activeTarget, simonStageId]);

  const render = (target: RealizationTarget) => {
    setBusy(true);
    setError(null);
    designRunsService.realize(runId, target)
      .then(refresh)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(false));
  };

  if (!run) return <div className="p-6 text-sm text-gray-500">Loading…</div>;

  const approved = run.design_status === 'approved';
  const simon = run.realizations?.simon;
  const talaera = run.realizations?.talaera;
  const simonStates = simonStageStates(run, doc);

  const targetChip = (target: RealizationTarget) => {
    const realization = run.realizations?.[target];
    if (!realization?.status) return <Chip tone="gray">not rendered</Chip>;
    const stale = (realization.design_version ?? 0) < run.design_version;
    if (stale) return <Chip tone="amber">stale — built from v{realization.design_version}</Chip>;
    if (realization.status === 'failed') return <Chip tone="red">failed</Chip>;
    if (target === 'simon') {
      return realization.status === 'package_ready'
        ? <Chip tone="green">package ready</Chip>
        : <Chip tone="blue">rendering…</Chip>;
    }
    return realization.status === 'rendered'
      ? <Chip tone="green">rendered</Chip>
      : <Chip tone="blue">building lessons…</Chip>;
  };

  const simonDetail = (() => {
    switch (simonStageId) {
      case 'simon_outline': return <OutlineDetail doc={doc} />;
      case 'simon_drafting': return <DraftingDetail doc={doc} />;
      case 'simon_level_check': return <LevelCheckDetail doc={doc} />;
      case 'simon_validate': return <ValidateDetail doc={doc} />;
      case 'simon_assets': return <AssetsDetail doc={doc} />;
      case 'simon_videos': return <VideosDetail doc={doc} />;
      case 'simon_compile':
        return <PackageDetail run={run} doc={doc} onRefresh={refresh} setError={setError} />;
      default:
        return <div className="text-sm text-gray-500">Select a stage to view details.</div>;
    }
  })();

  const activeRealization = activeTarget ? run.realizations?.[activeTarget] : undefined;

  return (
    <div className="p-6">
      <div className="flex items-center mb-4">
        <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700 shrink-0">
          &larr; {courseTitle}
        </button>
        <h2 className="text-lg font-semibold text-gray-900 text-center flex-1">Render status</h2>
        <div className="text-xs text-gray-500 shrink-0">
          Design v{run.design_version}
          {run.approved_version ? ` · approved v${run.approved_version}` : ''}
        </div>
      </div>

      {error && (
        <div className="p-3 mb-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">{error}</div>
      )}
      {run.last_error && (
        <div className="p-3 mb-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700 whitespace-pre-line">{run.last_error}</div>
      )}

      {/* Target tabs */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center bg-gray-100 rounded-md p-0.5 text-sm">
          {([['simon', 'Simon (OpenSesame)'], ['talaera', 'Talaera platform']] as const).map(([t, label]) => (
            <button key={t} type="button"
              className={`px-3 py-1.5 rounded transition-all flex items-center gap-2 ${activeTarget === t ? 'bg-white shadow-sm text-gray-900 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTarget(t)}>
              {label} {targetChip(t)}
            </button>
          ))}
        </div>
        {activeTarget && activeRealization?.status && activeRealization.status !== 'realizing' && (
          <button className="text-xs text-gray-400 hover:text-gray-600" disabled={busy}
            onClick={() => render(activeTarget)}>
            ↻ Re-render {activeTarget === 'simon' ? 'Simon package' : 'Talaera course'}
          </button>
        )}
      </div>

      {/* Simon: classic pipeline layout — stage cards left, stage data right */}
      {activeTarget === 'simon' && (
        !simon?.status ? (
          <DetailPanel title="Simon (OpenSesame)">
            <p className="text-sm text-gray-600 mb-3">
              Renders the approved design into a Build Package — a build spec plus an
              assets.zip (audio, images, HeyGen scenario videos) — for the claude.ai
              Chrome build in Simon.
            </p>
            <RenderButton label="Render for Simon" disabled={busy || !approved}
              onClick={() => render('simon')} />
            {!approved && (
              <div className="text-xs text-amber-600 mt-2">The design must be approved first.</div>
            )}
          </DetailPanel>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-3">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Pipeline</h3>
              <div className="relative">
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
                {SIMON_STEPS.map((step, index) => {
                  const state = simonStates[step.id];
                  return (
                    <PipelineCard key={step.id} stageId={step.id} number={String(index + 1)}
                      name={step.name} status={state?.status ?? 'pending'}
                      statusText={state?.statusText} cost={state?.cost} runCount={state?.runCount}
                      selected={simonStageId === step.id}
                      onClick={() => setSimonStageId(step.id)} />
                  );
                })}
              </div>
            </div>
            <div className="lg:col-span-9">
              <div className="bg-white border rounded p-4">{simonDetail}</div>
            </div>
          </div>
        )
      )}

      {/* Talaera: the actual classic course workspace, pinned to the realized run */}
      {activeTarget === 'talaera' && (
        !talaera?.status ? (
          <DetailPanel title="Talaera platform">
            <p className="text-sm text-gray-600 mb-3">
              Builds every lesson through the classic course pipeline — the result lands
              in the same lesson dashboard you use in Course Generation, with full
              editing, regeneration, and preview per lesson.
            </p>
            <RenderButton label="Render for Talaera" disabled={busy || !approved}
              onClick={() => render('talaera')} />
            {!approved && (
              <div className="text-xs text-amber-600 mt-2">The design must be approved first.</div>
            )}
          </DetailPanel>
        ) : !talaera.course_run_id ? (
          <DetailPanel title="Talaera platform">
            <div className="text-sm text-gray-500">Creating the course run…</div>
          </DetailPanel>
        ) : (
          <CourseWorkspace courseId={run.course_id} runId={talaera.course_run_id}
            onError={setError} />
        )
      )}
    </div>
  );
}
